import managerFleetService from "@/services/mrfiktiv/services/fleet/managerFleetService";
import { MrfiktivFleetViewModelGen } from "@/services/mrfiktiv/v1/data-contracts";
import { restoreFromLocalStorage } from "@/lib/restoreFromLocalStorage";
import store from "@/store/VuexPlugin";
import { Action, Module, Mutation, VuexModule, getModule } from "vuex-module-decorators";
import { buildFleetTree, FleetTreeNode } from "@/lib/build-fleet-tree";
import { UserModule } from "../me-user.store";

@Module({
  dynamic: true,
  namespaced: true,
  name: "fleetManagerStore",
  store
})
export class FleetManagerStore extends VuexModule {
  // First load flag
  private _initialLoad: boolean = restoreFromLocalStorage("_initialLoad", "fleetManagerStore", false);

  // Fleet tree
  private _fleetTree: FleetTreeNode[] = [];

  get fleetTree(): FleetTreeNode[] {
    return this._fleetTree;
  }

  @Action
  async getFleetTree(data: { partnerId: string; fleetId: string }): Promise<MrfiktivFleetViewModelGen[]> {
    const response = await managerFleetService.get(data.partnerId, data.fleetId);

    const tree = buildFleetTree(response.data);

    this.context.commit("_mutateSelectedFleetId", data.fleetId);

    this.context.commit("_mutateFleetTree", tree);

    this.context.commit("_syncSelectedFleetIds", { fleetId: data.fleetId, ids: response.data.map((f) => f.id) });

    return response.data;
  }

  @Mutation
  _mutateFleetTree(tree: FleetTreeNode[]) {
    this._fleetTree = tree;
  }

  // Selected fleetIds
  private _selectedFleetIds: Record<string, string[]> = restoreFromLocalStorage(
    "_selectedFleetIds",
    "fleetManagerStore",
    {}
  );

  get selectedFleetIds() {
    if (!this._selectedFleetId) return [];
    return this._selectedFleetIds[this._selectedFleetId] ?? [];
  }

  @Action
  setSelectedFleetIds(ids: string[]): void {
    // Through the getter: a state field that starts out `undefined` is missing on `this` inside an action
    const fleetId = this.selectedFleetId;
    if (!fleetId) return;
    this.context.commit("_mutateSelectedFleetIds", { fleetId, ids });
  }

  @Mutation
  _mutateSelectedFleetIds(payload: { fleetId: string; ids: string[] }) {
    this._selectedFleetIds = { ...this._selectedFleetIds, [payload.fleetId]: payload.ids };
  }

  /**
   * Aligns the persisted selection of a fleet with the fleets its manager endpoint returns now.
   * No fleets means the fleet was archived (or the user no longer manages it): its selection is dropped,
   * so the views stop counting fleets that are gone, and it starts over with every fleet if it comes back.
   * Otherwise sub fleets that disappeared are removed from the selection, and a fleet without one selects all.
   */
  @Mutation
  _syncSelectedFleetIds(payload: { fleetId: string; ids: string[] }) {
    const { [payload.fleetId]: cached, ...others } = this._selectedFleetIds;

    if (!payload.ids.length) {
      this._selectedFleetIds = others;
      return;
    }

    const ids = cached ? cached.filter((id) => payload.ids.includes(id)) : payload.ids;
    this._selectedFleetIds = { ...others, [payload.fleetId]: ids };
  }

  @Mutation
  _resetSelectedFleetIds() {
    this._selectedFleetIds = {};
  }

  @Mutation
  _setInitialLoad(value: boolean) {
    this._initialLoad = value;
  }

  // Selected parent fleetId
  private _selectedFleetId: string | undefined = restoreFromLocalStorage(
    "_selectedFleetId",
    "fleetManagerStore",
    undefined
  );

  get selectedFleetId(): string | undefined {
    return this._selectedFleetId;
  }

  get selectedFleet(): MrfiktivFleetViewModelGen | null {
    return this.fleets.find((f) => f.id === this.selectedFleetId) ?? null;
  }

  @Action
  async setSelectedFleetId(data: { fleetId: string; partnerId: string }): Promise<MrfiktivFleetViewModelGen[]> {
    return this.context.dispatch("getFleetTree", data);
  }

  @Mutation
  _mutateSelectedFleetId(fleetId: string) {
    this._selectedFleetId = fleetId;
  }

  // All parent fleets
  private _fleets: MrfiktivFleetViewModelGen[] = [];

  get fleets(): MrfiktivFleetViewModelGen[] {
    return this._fleets;
  }

  get permissionFleets() {
    return this.fleets.filter((f) => UserModule.distinctFleetIds.includes(f.id));
  }

  @Action
  async loadParentFleets(data: { partnerId: string; fleetIds: string[] }) {
    const allFleets: MrfiktivFleetViewModelGen[] = [];
    const batchSize = 5;

    for (let i = 0; i < data.fleetIds.length; i += batchSize) {
      const batch = data.fleetIds.slice(i, i + batchSize);

      const batchPromises = batch.map((fleetId) => managerFleetService.get(data.partnerId, fleetId));

      const batchResponses = await Promise.all(batchPromises);

      batchResponses.forEach((response, j) => {
        allFleets.push(...response.data);
        // Same response `getFleetTree` gets, so an archived fleet drops its selection here too
        this.context.commit("_syncSelectedFleetIds", { fleetId: batch[j], ids: response.data.map((f) => f.id) });
      });
    }

    this.context.commit("_mutateFleets", allFleets);

    return allFleets;
  }

  @Mutation
  _mutateFleets(fleets: MrfiktivFleetViewModelGen[]) {
    this._fleets = fleets;
  }

  @Action
  reset() {
    this.context.commit("_resetSelectedFleetIds");
    this.context.commit("_mutateFleets", []);
    this.context.commit("_mutateFleetTree", []);
    this.context.commit("_mutateSelectedFleetId", undefined);
    this.context.commit("_setInitialLoad", false);
  }
}

export const FleetManagerModule = getModule(FleetManagerStore);
